/**
 * NsfOdOverrideDialog — account-scoped NSF/OD override with supervisor gate.
 *
 * Fires from: Products tab row action on a DDA.
 *
 * Role gate:
 *   - Supervisor+ (Supervisor/Manager/Compliance/Executive/Admin/SuperAdmin):
 *     can save directly — their own upn fills the supervisor slot.
 *   - Lesser roles: form requires supervisor UPN + initials fields to be
 *     populated before Save enables. This is the v3 replacement for the
 *     legacy "Supervisor override" paper route.
 *
 * Legacy references: NEWOD.pdf, NEWESI.pdf, Back on Track.pdf, Overdraft
 * Protection.pdf, NOFUND.pdf.
 */

import { useEffect, useMemo, useState } from 'react';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useNsfOdOverride, type NsfOverrideType, type NsfOverrideReason } from './useNsfOdOverride';
import type { AccountScopedMaintProps } from '../types';

const SUPERVISOR_ROLES = new Set<string>([
  'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin',
]);

const OVERRIDE_TYPES: ReadonlyArray<{ code: NsfOverrideType; label: string; needsAmount: boolean }> = [
  { code: 'fee-waiver',     label: 'Fee Waiver',          needsAmount: true  },
  { code: 'limit-increase', label: 'Overdraft Limit Increase', needsAmount: true  },
  { code: 'counter-reset',  label: 'Reset NSF Counter',   needsAmount: false },
  { code: 'back-on-track',  label: 'Back on Track Enrollment', needsAmount: false },
];

const REASONS: ReadonlyArray<{ code: NsfOverrideReason; label: string }> = [
  { code: 'courtesy',           label: 'Customer courtesy' },
  { code: 'back-on-track',      label: 'Back on Track program' },
  { code: 'manager-discretion', label: 'Manager discretion' },
  { code: 'bank-error',         label: 'Bank error (regulatory)' },
  { code: 'other',              label: 'Other (see notes)' },
];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--ink-100)',
  borderRadius: 'var(--r-sm)',
  fontSize: 13,
  fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-600)',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  fontWeight: 600,
  marginBottom: 4,
  display: 'block',
};

export function NsfOdOverrideDialog(props: AccountScopedMaintProps): JSX.Element | null {
  const {
    open, onClose, onSaved, cifId, acctId, acctType, prodDesc,
    officerCode = '000', readOnly = false,
  } = props;
  const { user } = useAuth();
  const { run } = useNsfOdOverride();

  const isSupervisor = useMemo((): boolean => {
    return Boolean(user?.primaryRole && SUPERVISOR_ROLES.has(user.primaryRole));
  }, [user]);

  const [overrideType, setOverrideType] = useState<NsfOverrideType>('fee-waiver');
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<NsfOverrideReason>('courtesy');
  const [notes, setNotes] = useState<string>('');
  const [supervisorUpn, setSupervisorUpn] = useState<string>('');
  const [supervisorInitials, setSupervisorInitials] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOverrideType('fee-waiver');
    setAmount('');
    setReason('courtesy');
    setNotes('');
    setSupervisorUpn(isSupervisor ? (user?.upn ?? '') : '');
    setSupervisorInitials('');
    setErr(null);
  }, [open, isSupervisor, user?.upn]);

  const typeMeta = OVERRIDE_TYPES.find((t) => t.code === overrideType)!;
  const parsedAmount = Number.parseFloat(amount || '0');

  const canSave = useMemo((): boolean => {
    if (readOnly || submitting) return false;
    if (typeMeta.needsAmount) {
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return false;
    }
    if (notes.trim().length === 0) return false;
    if (!supervisorUpn.trim()) return false;
    if (!supervisorInitials.trim() || supervisorInitials.trim().length < 2) return false;
    return true;
  }, [readOnly, submitting, typeMeta, parsedAmount, notes, supervisorUpn, supervisorInitials]);

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSubmitting(true);
    setErr(null);
    const result = await run({
      cifId,
      acctId,
      acctType,
      overrideType,
      amount: typeMeta.needsAmount ? parsedAmount : 0,
      reasonCode: reason,
      notes: notes.trim(),
      officerCode,
      supervisorUpn: supervisorUpn.trim(),
      supervisorInitials: supervisorInitials.trim().toUpperCase(),
    });
    setSubmitting(false);
    if (!result.ok) {
      setErr(result.error ?? 'Unknown error');
      return;
    }
    onSaved?.();
    onClose();
  };

  if (!open) return null;

  return (
    <ModalShell
      open={open}
      onClose={submitting ? () => { /* block */ } : onClose}
      title={`NSF / OD Override — ${acctId}${prodDesc ? ' (' + prodDesc + ')' : ''}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          {!readOnly && (
            <Button variant="danger" onClick={() => { void handleSave(); }} disabled={!canSave}>
              {submitting ? 'Applying…' : 'Apply Override'}
            </Button>
          )}
        </>
      }
    >
      {readOnly && (
        <Banner tone="warn" title="Read-only">
          You do not have the NSF-Override grant.
        </Banner>
      )}

      <Banner tone="warn" title="Supervisor approval required">
        Every NSF/OD override is audit-logged. Overrides exceeding $500 auto-create a compliance review task.
        {!isSupervisor && ' You are not a Supervisor — the approver must sign below.'}
      </Banner>

      {err && <Banner tone="err" title="Override failed">{err}</Banner>}

      <div>
        <label style={labelStyle}>Override Type *</label>
        <select
          value={overrideType}
          onChange={(e) => { setOverrideType(e.target.value as NsfOverrideType); }}
          disabled={readOnly || submitting}
          style={inputStyle}
        >
          {OVERRIDE_TYPES.map((t) => (
            <option key={t.code} value={t.code}>{t.label}</option>
          ))}
        </select>
      </div>

      {typeMeta.needsAmount && (
        <div>
          <label style={labelStyle}>Amount (USD) *</label>
          <input
            type="number"
            min={0}
            step={0.01}
            value={amount}
            onChange={(e) => { setAmount(e.target.value); }}
            disabled={readOnly || submitting}
            style={{ ...inputStyle, maxWidth: 200 }}
          />
        </div>
      )}

      <div>
        <label style={labelStyle}>Reason Code *</label>
        <select
          value={reason}
          onChange={(e) => { setReason(e.target.value as NsfOverrideReason); }}
          disabled={readOnly || submitting}
          style={inputStyle}
        >
          {REASONS.map((r) => (
            <option key={r.code} value={r.code}>{r.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label style={labelStyle}>Notes * (required, max 250 chars)</label>
        <textarea
          value={notes}
          onChange={(e) => { setNotes(e.target.value.slice(0, 250)); }}
          disabled={readOnly || submitting}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>{notes.length}/250</div>
      </div>

      <div style={{ borderTop: '1px solid var(--ink-100)', paddingTop: 'var(--sp-5)' }}>
        <label style={{ ...labelStyle, color: 'var(--pcb-red)' }}>Supervisor Approval</label>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--sp-4)' }}>
          <div>
            <label style={labelStyle}>Supervisor UPN *</label>
            <input
              type="email"
              value={supervisorUpn}
              onChange={(e) => { setSupervisorUpn(e.target.value); }}
              disabled={readOnly || submitting || isSupervisor}
              placeholder="supervisor@mypcb.com"
              style={inputStyle}
            />
            {isSupervisor && (
              <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>
                Auto-filled — you are {user?.primaryRole}.
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Initials *</label>
            <input
              type="text"
              value={supervisorInitials}
              onChange={(e) => { setSupervisorInitials(e.target.value.toUpperCase()); }}
              disabled={readOnly || submitting}
              maxLength={4}
              style={inputStyle}
            />
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
